import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import GoogleAuthButton from '@/components/auth/GoogleAuthButton';
import Header from '@/components/layout/Header';
import { Loader2, Mail, Lock, Shield } from 'lucide-react';

const Index = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect to dashboard if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-10 w-10 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Add a subtle gradient background */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-background to-muted/30">
        <div className="w-full max-w-xl py-16 flex flex-col items-center text-center animate-slide-up">
          <div className="mb-10 md:mb-12">
            
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-5 md:mb-6 leading-tight">
              Manage Your Gmail Attachments <span className="text-primary">Effortlessly</span>
            </h1>
            
          </div>
          
          <div className="w-full max-w-xs mb-12">
            <GoogleAuthButton />
          </div>
          
          {/* Feature Cards - Glassmorphism */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 w-full max-w-3xl mt-12">
            {/* Card 1 */}
            <div className="flex flex-col items-center p-5 rounded-lg border transition-all duration-300 hover:shadow-lg 
                        bg-white/30 backdrop-blur-lg border-white/40 
                        dark:bg-black/20 dark:border-white/10">
              <div className="w-12 h-12 flex items-center justify-center mb-4 rounded-full bg-primary/10">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-semibold text-base mb-1">Find Attachments Fast</h3>
              <p className="text-sm text-muted-foreground text-center">
                Browse, filter, and search all your attachments.
              </p>
            </div>
            
            {/* Card 2 */}
            <div className="flex flex-col items-center p-5 rounded-lg border transition-all duration-300 hover:shadow-lg 
                        bg-white/30 backdrop-blur-lg border-white/40 
                        dark:bg-black/20 dark:border-white/10">
              <div className="w-12 h-12 flex items-center justify-center mb-4 rounded-full bg-primary/10">
                <Lock className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-semibold text-base mb-1">Secure Access</h3>
              <p className="text-sm text-muted-foreground text-center">
                Your data is never stored on our servers
              </p>
            </div>
            
            {/* Card 3 */}
            <div className="flex flex-col items-center p-5 rounded-lg border transition-all duration-300 hover:shadow-lg 
                        bg-white/30 backdrop-blur-lg border-white/40 
                        dark:bg-black/20 dark:border-white/10">
              <div className="w-12 h-12 flex items-center justify-center mb-4 rounded-full bg-primary/10">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-semibold text-base mb-1">Privacy First</h3>
              <p className="text-sm text-muted-foreground text-center">
                We only request the permissions we need
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <footer className="py-8 border-t border-border">
        <div className="container-pad flex justify-center">
          <p className="text-sm text-muted-foreground">
            Gmail Attachment Manager &copy; {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
